/**
 * Site-wide vehicle garage.
 *
 * Single source of truth for the shopper's saved vehicles/sizes and their
 * currently active one. State lives in localStorage on the shopper's own
 * device — nothing is sent to the store, so no consent banner is required.
 *
 * Public API: window.ThemeGarage
 *   list()               -> [{label, tag, type}]  most recent first
 *   add(entry)           -> stores and returns the list
 *   remove(tag)          -> drops an entry (and clears active if it matched)
 *   getActive()          -> {label, tag, type} | null
 *   setActive(entry)     -> marks one entry active (adds it if new)
 *   clearActive()        -> unsets active, keeps it in the list
 *   subscribe(fn)        -> fn() on every change; returns an unsubscribe fn
 */
(function () {
  if (window.ThemeGarage) return;

  var KEY_LIST = 'tf_garage_v1';
  var KEY_ACTIVE = 'tf_active_v1';
  var MAX = 6;
  var listeners = [];

  function read(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* storage unavailable (private mode, quota) — degrade to no persistence */
    }
  }

  function emit() {
    listeners.forEach(function (fn) {
      try {
        fn();
      } catch (e) {
        /* a bad listener must not break the others */
      }
    });
  }

  var Garage = {
    list: function () {
      var l = read(KEY_LIST, []);
      return Array.isArray(l) ? l : [];
    },

    add: function (entry) {
      if (!entry || !entry.tag) return Garage.list();
      var list = Garage.list().filter(function (x) {
        return x.tag !== entry.tag;
      });
      list.unshift({ label: entry.label || entry.tag, tag: entry.tag, type: entry.type || 'vehicle' });
      write(KEY_LIST, list.slice(0, MAX));
      emit();
      return Garage.list();
    },

    remove: function (tag) {
      write(
        KEY_LIST,
        Garage.list().filter(function (x) {
          return x.tag !== tag;
        })
      );
      var active = Garage.getActive();
      if (active && active.tag === tag) {
        write(KEY_ACTIVE, null);
      }
      emit();
      return Garage.list();
    },

    getActive: function () {
      var a = read(KEY_ACTIVE, null);
      return a && a.tag ? a : null;
    },

    setActive: function (entry) {
      if (!entry || !entry.tag) return null;
      var normalised = { label: entry.label || entry.tag, tag: entry.tag, type: entry.type || 'vehicle' };
      Garage.add(normalised);
      write(KEY_ACTIVE, normalised);
      emit();
      return normalised;
    },

    clearActive: function () {
      write(KEY_ACTIVE, null);
      emit();
    },

    subscribe: function (fn) {
      listeners.push(fn);
      return function () {
        listeners = listeners.filter(function (f) {
          return f !== fn;
        });
      };
    },
  };

  window.ThemeGarage = Garage;

  // Keep tabs in sync when storage changes in another tab.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY_LIST || e.key === KEY_ACTIVE) emit();
  });

  // ---------------------------------------------------------------
  // Bar rendering
  // ---------------------------------------------------------------
  function filterUrl(base, tag) {
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + 'filter.p.tag=' + encodeURIComponent(tag);
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function renderBar(bar) {
    var active = Garage.getActive();
    var body = bar.querySelector('[data-garage-body]');
    if (!body) return;

    body.innerHTML = '';

    if (!active) {
      bar.hidden = true;
      return;
    }
    bar.hidden = false;

    var isCollection = bar.dataset.collectionUrl && bar.dataset.collectionUrl.length > 0;
    var filterApplied = bar.dataset.filterTag === active.tag;

    var label = el('span', 'garage-bar__label');
    label.appendChild(el('span', 'garage-bar__eyebrow', filterApplied ? 'Showing parts for' : 'Shopping for'));
    label.appendChild(el('strong', 'garage-bar__vehicle', active.label));
    body.appendChild(label);

    var actions = el('span', 'garage-bar__actions');

    if (isCollection && !filterApplied) {
      var apply = el('a', 'garage-bar__btn', bar.dataset.applyLabel || 'Show parts for my vehicle');
      apply.href = filterUrl(bar.dataset.collectionUrl, active.tag);
      actions.appendChild(apply);
    }

    if (isCollection && filterApplied) {
      var showAll = el('a', 'garage-bar__btn garage-bar__btn--ghost', bar.dataset.showAllLabel || 'Show all');
      showAll.href = bar.dataset.collectionUrl;
      actions.appendChild(showAll);
    }

    var change = el('button', 'garage-bar__link', bar.dataset.changeLabel || 'Change vehicle');
    change.type = 'button';
    change.addEventListener('click', function () {
      Garage.clearActive();
      var home = bar.dataset.finderUrl;
      if (home) window.location.href = home;
    });
    actions.appendChild(change);

    body.appendChild(actions);
  }

  function renderAll() {
    document.querySelectorAll('[data-garage-bar]').forEach(renderBar);
  }

  function autoApply() {
    document.querySelectorAll('[data-garage-bar]').forEach(function (bar) {
      if (bar.dataset.autoApply !== 'true') return;
      if (!bar.dataset.collectionUrl) return;
      if (bar.dataset.filterTag) return; // a filter is already in play
      var active = Garage.getActive();
      if (!active) return;
      // Only redirect once per session per tag, so Back stays usable.
      var key = 'tf_autoapplied_' + active.tag;
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
      } catch (e) {
        return;
      }
      window.location.replace(filterUrl(bar.dataset.collectionUrl, active.tag));
    });
  }

  function init() {
    renderAll();
    autoApply();
    Garage.subscribe(renderAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
